import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import PlayIcon from '../Icons/Action/PlayIcon';
import type { Video } from '@/types/video';
import Link from 'next/link';
import { ROUTES } from '@/config/routes';
import { T } from '@tolgee/react';
import { useVideoService } from '@/hooks/useVideoService';

interface VideoCardsSliderProps {
  videos: Video[];
  author?: string;
  languages?: string;
  cardAlwayVisible?: boolean;
  onHover?: (imageUrl: string | null) => void;
  isInsideLink?: boolean;
}

const settings = {
  dots: false,
  infinite: false,
  arrows: false,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 1280,
      settings: {
        slidesToShow: 3.2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2.5,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1.2,
        slidesToScroll: 1,
      },
    },
  ],
};

const VideoCardsSlider: React.FC<VideoCardsSliderProps> = ({
  videos,
  author,
  languages,
  cardAlwayVisible = false,
  onHover,
  isInsideLink = false,
}) => {
  const [durations, setDurations] = useState<number[]>([]);
  const [errors, setErrors] = useState<string | null>(null);
  const { fetchVideoDuration } = useVideoService();

  useEffect(() => {
    const loadDurations = async () => {
      try {
        const durationPromises = videos.map((video) => fetchVideoDuration(video.videoUrl || ''));
        const results = await Promise.all(durationPromises);
        setDurations(results.map((res) => res.duration));
      } catch (error) {
        setErrors('Error fetching video durations');
        console.error(error);
      }
    };

    void loadDurations();
  }, [videos]);

  return (
    <Slider {...settings} className="w-full video-card">
      {videos.map((video, index) => {
        const href = ROUTES.VIDEO(video.id?.toString() || '', video.title);
        const content = (
          <div className="relative group cursor-pointer">
            <div
              className={`bg-white h-32 bg-white/50 backdrop-blur-[9.4px] p-4 rounded-lg transition-all transform relative overflow-hidden ${
                cardAlwayVisible ? 'h-52' : 'group-hover:h-52'
              }`}
            >
              <div
                className={`flex justify-between items-center overflow-hidden ${
                  cardAlwayVisible ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'
                } transition-all duration-300 ${
                  cardAlwayVisible ? '' : 'group-hover:translate-y-0 group-hover:opacity-100'
                }`}
              >
                <span className="text-primary text-sm font-semibold uppercase">
                  {durations[index] !== undefined ? (
                    <>
                      <T keyName="video_details.video_type.video.label" />
                      {' • '}
                      <T
                        keyName="video_details.video_duration.minute.label"
                        params={{ count: Math.round(durations[index] / 60) }}
                      />
                    </>
                  ) : (
                    <T keyName="loading.label" />
                  )}
                  {' • '}
                  {languages?.toUpperCase()}
                </span>
                <PlayIcon />
              </div>
              <h3
                className={`text-lg mt-0 transition-all duration-300 ${
                  cardAlwayVisible ? 'mt-12' : 'group-hover:mt-12'
                }`}
              >
                {video.title}
              </h3>
              <p
                className={`text-base mt-2 overflow-hidden transform transition-all duration-300 ${
                  cardAlwayVisible
                    ? 'translate-y-0 opacity-50'
                    : 'translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-50'
                }`}
              >
                {author || video.author}
              </p>
            </div>
          </div>
        );

        if (isInsideLink) {
          return (
            <div key={index} onMouseEnter={() => onHover?.(video.src || null)}>
              {content}
            </div>
          );
        }

        return (
          <Link href={href} key={index} passHref onMouseEnter={() => onHover?.(video.src || null)}>
            {content}
          </Link>
        );
      })}
      {errors && <p className="text-red-500 text-sm">{errors}</p>}
    </Slider>
  );
};

export default VideoCardsSlider;
